import React, { useState } from 'react';
import WorkspaceShell from './WorkspaceShell';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import {
  useGetWorkspaceAdvisorsQuery,
  useGetAdvisorAvailabilityQuery,
  useSaveAdvisorAvailabilityMutation,
  useGetWorkspaceBookingsQuery,
  useCreateWorkspaceBookingMutation,
  useUpdateBookingStatusMutation,
} from '../../features/api/apiSlice';
import { useSelector } from 'react-redux';
import { selectCustomerId } from '../../features/auth/authSlice';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const BookingsPage = () => {
  const myId = useSelector(selectCustomerId);
  const { data: advisors = [] } = useGetWorkspaceAdvisorsQuery();
  const { data: mySlots = [], refetch: refetchSlots } = useGetAdvisorAvailabilityQuery(
    { advisor_id: myId },
    { skip: !myId }
  );
  const { data: bookings = [], refetch } = useGetWorkspaceBookingsQuery({ as: 'all' });
  const [saveAvailability, { isLoading: savingSlots }] = useSaveAdvisorAvailabilityMutation();
  const [createBooking, { isLoading: booking }] = useCreateWorkspaceBookingMutation();
  const [updateStatus] = useUpdateBookingStatusMutation();

  const [slotForm, setSlotForm] = useState({ weekday: 1, start_time: '09:00', end_time: '17:00', slot_minutes: 30 });
  const [localSlots, setLocalSlots] = useState([]);
  const [bookForm, setBookForm] = useState({
    advisor_id: '',
    topic: '',
    starts_at: '',
    duration_minutes: 30,
    fee_type: 'free',
    fee_amount: 0,
    meeting_mode: 'video',
    notes: '',
  });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  React.useEffect(() => {
    if (mySlots?.length) {
      setLocalSlots(
        mySlots.map((s) => ({
          weekday: s.weekday,
          start_time: String(s.start_time).slice(0, 5),
          end_time: String(s.end_time).slice(0, 5),
          slot_minutes: s.slot_minutes || 30,
        }))
      );
    }
  }, [mySlots]);

  const addSlot = () => {
    setLocalSlots((prev) => [...prev, { ...slotForm }]);
  };

  const saveSlots = async () => {
    setErr('');
    setMsg('');
    try {
      await saveAvailability({ slots: localSlots.length ? localSlots : [slotForm] }).unwrap();
      setMsg('Availability calendar saved.');
      refetchSlots();
    } catch (e) {
      setErr(e?.data?.message || 'Failed to save availability');
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    try {
      await createBooking({
        ...bookForm,
        fee_amount: bookForm.fee_type === 'paid' ? Number(bookForm.fee_amount || 0) : 0,
      }).unwrap();
      setMsg('Consultation booked.');
      setBookForm((f) => ({ ...f, topic: '', starts_at: '', notes: '' }));
      refetch();
    } catch (e2) {
      setErr(e2?.data?.message || 'Booking failed');
    }
  };

  return (
    <WorkspaceShell
      title="Advisory Booking"
      subtitle="Set your availability (Calendly-style) and book paid or free consultations via video or chat."
    >
      {msg && <p className="text-success text-sm mb-3">{msg}</p>}
      {err && <p className="text-error text-sm mb-3">{err}</p>}

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-xl border border-border bg-card p-5 space-y-3">
          <h2 className="font-semibold text-lg">My availability</h2>
          <div className="grid grid-cols-2 gap-2">
            <Select
              label="Weekday"
              options={WEEKDAYS.map((d, i) => ({ value: String(i), label: d }))}
              value={String(slotForm.weekday)}
              onChange={(v) => setSlotForm({ ...slotForm, weekday: Number(v) })}
            />
            <Input label="Slot mins" type="number" value={slotForm.slot_minutes}
              onChange={(e) => setSlotForm({ ...slotForm, slot_minutes: Number(e.target.value) })} />
            <Input label="Start" type="time" value={slotForm.start_time}
              onChange={(e) => setSlotForm({ ...slotForm, start_time: e.target.value })} />
            <Input label="End" type="time" value={slotForm.end_time}
              onChange={(e) => setSlotForm({ ...slotForm, end_time: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={addSlot}>Add slot</Button>
            <Button type="button" size="sm" onClick={saveSlots} loading={savingSlots}>Save calendar</Button>
          </div>
          <ul className="text-sm space-y-1">
            {localSlots.map((s, i) => (
              <li key={i} className="flex justify-between border-b border-border py-1">
                <span>{WEEKDAYS[s.weekday]} {s.start_time}–{s.end_time} ({s.slot_minutes}m)</span>
                <button type="button" className="text-error text-xs" onClick={() => setLocalSlots((p) => p.filter((_, j) => j !== i))}>Remove</button>
              </li>
            ))}
            {localSlots.length === 0 && <li className="text-muted-foreground">No slots configured.</li>}
          </ul>
        </section>

        <section className="rounded-xl border border-border bg-card p-5 space-y-3">
          <h2 className="font-semibold text-lg">Book a consultation</h2>
          <form onSubmit={submitBooking} className="space-y-3">
            <Select
              label="Advisor / agent"
              options={[
                { value: '', label: 'Select…' },
                ...advisors.map((a) => ({
                  value: String(a.id),
                  label: `${a.name}${a.verification_status === 'verified' ? ' ✓' : ''} (${a.account_role})`,
                })),
              ]}
              value={bookForm.advisor_id}
              onChange={(v) => setBookForm({ ...bookForm, advisor_id: v })}
              required
            />
            <Input label="Topic" required value={bookForm.topic}
              onChange={(e) => setBookForm({ ...bookForm, topic: e.target.value })} />
            <Input label="Starts at" type="datetime-local" required value={bookForm.starts_at}
              onChange={(e) => setBookForm({ ...bookForm, starts_at: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Select
                label="Mode"
                options={[
                  { value: 'video', label: 'Video call' },
                  { value: 'chat', label: 'Chat' },
                  { value: 'in_person', label: 'In person' },
                ]}
                value={bookForm.meeting_mode}
                onChange={(v) => setBookForm({ ...bookForm, meeting_mode: v })}
              />
              <Select
                label="Fee"
                options={[
                  { value: 'free', label: 'Free' },
                  { value: 'paid', label: 'Paid' },
                ]}
                value={bookForm.fee_type}
                onChange={(v) => setBookForm({ ...bookForm, fee_type: v })}
              />
            </div>
            {bookForm.fee_type === 'paid' && (
              <Input label="Fee amount (UGX)" type="number" value={bookForm.fee_amount}
                onChange={(e) => setBookForm({ ...bookForm, fee_amount: e.target.value })} />
            )}
            <Button type="submit" loading={booking} disabled={booking || !bookForm.advisor_id}>
              Book session
            </Button>
          </form>
        </section>
      </div>

      <section className="mt-8">
        <h2 className="font-semibold text-lg mb-3">Your bookings</h2>
        {bookings.length === 0 ? (
          <p className="text-muted-foreground text-sm">No bookings yet.</p>
        ) : (
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} className="rounded-xl border border-border bg-card p-4 flex flex-wrap justify-between gap-3">
                <div>
                  <p className="font-medium">{b.topic}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(b.starts_at).toLocaleString()} · {b.meeting_mode} · {b.fee_type}
                    {b.fee_type === 'paid' ? ` ${b.currency} ${Number(b.fee_amount).toLocaleString()}` : ''}
                  </p>
                  <p className="text-xs">
                    Advisor: {b.advisor?.name || '—'} · Client: {b.client_name || b.client?.name || '—'}
                  </p>
                  {b.meeting_link && (
                    <a href={b.meeting_link} className="text-xs text-primary underline" target="_blank" rel="noreferrer">
                      Meeting link
                    </a>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-muted capitalize">{b.status}</span>
                  {['pending', 'confirmed'].includes(b.status) && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => updateStatus({ id: b.id, status: 'completed' })}>Complete</Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus({ id: b.id, status: 'cancelled' })}>Cancel</Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </WorkspaceShell>
  );
};

export default BookingsPage;
